<script lang="ts" setup>
import type { SuggestionGroup } from '../../../composables/useLocationAutocomplete';
import type { SearchSuggestDialogEmits } from '../../../composables/useNewSearchSuggest';
import type { NewSearchSuggestProps } from '../../SearchSuggest';
import type { SearchSuggestDialogPanelHostProps } from '../../SearchSuggest/searchSuggestDialogPanelProps';

import IconMaterialSymbolsLightCancel from '~icons/material-symbols-light/cancel';
import IconMaterialSymbolsLightSearch from '~icons/material-symbols-light/search';

import { useNewSearchSuggest } from '../../../composables/useNewSearchSuggest';
import { useSingleFieldSuggest } from '../../../composables/useSingleFieldSuggest';
import { cn } from '../../../utils';
import { SEARCH_PLACEHOLDERS } from '../../SearchSuggest';
import { useSearchSuggestDialogDismissRestore } from '../../SearchSuggest/composables/useSearchSuggestDialogDismissRestore';
import { useSearchSuggestDialogParentLabelTriggerState } from '../../SearchSuggest/composables/useSearchSuggestDialogParentLabelTriggerState';
import { useSearchSuggestDialogScrollLock } from '../../SearchSuggest/composables/useSearchSuggestDialogScrollLock';
import { useSearchSuggestDialogSharedHandlers } from '../../SearchSuggest/composables/useSearchSuggestDialogSharedHandlers';
import SearchSuggestDialogPanel from '../../SearchSuggest/SearchSuggestDialogPanel.vue';
import { getSearchSuggestVariantStyles } from '../../SearchSuggest/variants';

/** Dialog de busca exclusivo do Shorts — não alterar SuggestDialog (SRP). */
const props = withDefaults(
	defineProps<
		NewSearchSuggestProps & {
			placeholderClasses?: string;
			/** Placeholder no modal (trigger + input focado). */
			focusedPlaceholder?: string;
			/**
			 * Quando false, não aplica `scroll-locked` no body (telas já `fixed`,
			 * ex.: reel em `/shorts/:id`).
			 */
			lockBodyScroll?: boolean;
			/**
			 * Classes extras no overlay do dialog (use literais completas, ex. `z-[260]`,
			 * para o Tailwind incluir no bundle).
			 */
			dialogOverlayExtraClass?: string;
			/** Idem para o conteúdo (ex. um nível acima do overlay). */
			dialogContentExtraClass?: string;
		}
	>(),
	{
		isFetchingPropertyByCodeSearch: false,
		placeholders: () => [...SEARCH_PLACEHOLDERS.animated],
		showInputButton: true,
		variant: 'dark',
		lockBodyScroll: true,
		emitEmptyStateOnlyAfterAutocomplete: true,
		includeTextSearchedInSelectionPayload: true
	}
);

const variantStyles = computed(() =>
	getSearchSuggestVariantStyles(props.variant)
);

const emit = defineEmits<SearchSuggestDialogEmits>();

const isOpen = defineModel<boolean>('isOpen', { required: true });
const selectedValue = ref<SuggestionGroup>();

const { selectedMacroregion: globalSelectedMacroregion } =
	useSingleFieldSuggest();

const {
	searchTerm,
	modelValue,
	hasSelectedValue,
	isUpdatingFromWatch,
	backupSearchTerm,
	hasMinSearchTerm,
	searchByCodeOption,
	shouldShowCodeGroup,
	shouldShowEmptyState,
	shouldShowLastResearchs,
	locationAutocompleteData,
	loading,
	dynamicPlaceholder,
	placeholders: computedPlaceholders,
	filteredLastResearchs,
	removeResearchs,
	clear,
	inputClasses,
	getDisplayValue,
	handleKeyDown,
	handleModelValueUpdate: baseHandleModelValueUpdate,
	executeLocationAutocomplete,
	handleApplySearchTerm
} = useNewSearchSuggest({
	props,
	emit,
	isDropdownOpen: isOpen,
	macroregion: globalSelectedMacroregion,
	providers: props.providers,
	selectOnly: props.selectOnly,
	bus: props.bus
});

const { restoreBodyScroll } = useSearchSuggestDialogScrollLock(isOpen, {
	enabled: () => props.lockBodyScroll
});

const parentSearchTerm = computed(() => props.searchTerm);

const { onCommandDialogOpenChange, closeDialog } =
	useSearchSuggestDialogDismissRestore({
		parentSearchTerm,
		emitUpdateSearchTerm: v => emit('update:search-term', v),
		searchTerm,
		modelValue,
		isUpdatingFromWatch,
		hasSelectedValue,
		backupSearchTerm,
		handleApplySearchTerm,
		isOpen
	});

function afterSelectSuggestion() {
	isOpen.value = false;
}

const { openDialog, handleModelValueUpdate, handleInputClear } =
	useSearchSuggestDialogSharedHandlers({
		isOpen,
		props,
		searchTerm,
		isUpdatingFromWatch,
		clear,
		executeLocationAutocomplete,
		handleApplySearchTerm,
		baseHandleModelValueUpdate,
		afterSelectSuggestion,
		closeModal: closeDialog,
		triggerClearUsesFullClear: false
	});

const { hasSelectionLabel, selectedDisplayText, dialogPanelPlaceholderProps } =
	useSearchSuggestDialogParentLabelTriggerState({
		placeholders: () => props.placeholders,
		searchTermProp: () => props.searchTerm,
		modelValue,
		searchTermInternal: searchTerm,
		getDisplayValue,
		dynamicPlaceholder,
		computedPlaceholders,
		defaultDialogPlaceholder: SEARCH_PLACEHOLDERS.dialog,
		focusedFieldPlaceholder: () => props.focusedPlaceholder
	});

const dialogPanelHost = computed<SearchSuggestDialogPanelHostProps>(() => ({
	filterProps: props,
	isOpen: isOpen.value,
	variantStyles: variantStyles.value,
	...dialogPanelPlaceholderProps.value,
	loading: loading.value,
	hasMinSearchTerm: hasMinSearchTerm.value,
	shouldShowEmptyState: props.hideCodeSearch
		? !loading.value &&
			hasMinSearchTerm.value &&
			!locationAutocompleteData.value?.length
		: shouldShowEmptyState.value,
	shouldShowLastResearchs: shouldShowLastResearchs.value,
	shouldShowCodeGroup: shouldShowCodeGroup.value && !props.hideCodeSearch,
	searchByCodeOption: props.hideCodeSearch ? [] : searchByCodeOption.value,
	filteredLastResearchs: filteredLastResearchs.value,
	locationAutocompleteData: locationAutocompleteData.value,
	getDisplayValue,
	inputClasses: inputClasses.value,
	onCommandOpenChange: onCommandDialogOpenChange,
	onDialogBack: closeDialog,
	onDialogInputClear: handleInputClear,
	onModelValueUpdate: handleModelValueUpdate,
	onKeyDown: handleKeyDown,
	onRemoveResearch: removeResearchs,
	dialogOverlayExtraClass: props.dialogOverlayExtraClass,
	dialogContentExtraClass: props.dialogContentExtraClass
}));

onBeforeUnmount(() => {
	if (isOpen.value) {
		restoreBodyScroll();
	}
});

const panelRef =
	useTemplateRef<InstanceType<typeof SearchSuggestDialogPanel>>('panelRef');

function focusInput() {
	panelRef.value?.inputEl?.focus();
}

defineExpose({ focusInput });
</script>
<template>
	<div class="relative h-full max-w-full overflow-hidden">
		<div
			v-if="showInputButton"
			:class="cn('flex size-full items-center', inputWrapperClasses)"
		>
			<slot name="input-prefix" />
			<slot
				name="trigger"
				:open="openDialog"
				:clear="handleInputClear"
				:is-open="isOpen"
			>
				<button
					:class="cn(variantStyles.buttonInput, buttonInputClasses)"
					@click="openDialog"
				>
					<span
						:class="
							cn(
								hasSelectionLabel
									? variantStyles.resultText
									: variantStyles.placeholderText,
								'flex-1 text-left truncate',
								!hasSelectionLabel ? placeholderClasses : undefined
							)
						"
					>
						{{ selectedDisplayText }}
					</span>
				</button>
				<div class="ml-auto flex items-center">
					<slot name="suffix">
						<div
							:class="
								cn(
									'flex h-full w-7 items-center justify-end',
									inputClearIconWrapperClasses
								)
							"
						>
							<IconMaterialSymbolsLightCancel
								v-show="hasSelectionLabel"
								:class="
									cn(
										'size-5 min-w-5 cursor-pointer text-white-dark-3',
										inputClearIconClasses
									)
								"
								@click.stop="handleInputClear"
							/>
						</div>
						<div
							:class="
								cn(
									'grid h-full w-[62px] place-items-center',
									inputSearchIconWrapperClasses
								)
							"
						>
							<IconMaterialSymbolsLightSearch
								:class="
									cn('size-7', variantStyles.searchIcon, inputSearchIconClasses)
								"
							/>
						</div>
					</slot>
				</div>
			</slot>
		</div>

		<ClientOnly>
			<SearchSuggestDialogPanel
				ref="panelRef"
				v-model:search-term="searchTerm"
				v-model:model-value="modelValue"
				v-model:selected-value="selectedValue"
				v-bind="dialogPanelHost"
			>
				<template v-if="$slots['empty-state-icon']" #empty-state-icon>
					<slot name="empty-state-icon" />
				</template>
			</SearchSuggestDialogPanel>
		</ClientOnly>
	</div>
</template>
